/**
 * ★ Fase 92/93 (Doc 02) — Blocchi riutilizzabili per le mini-landing scure
 * (Villa Flora Bali · Green Paradise Lombok). Palette funnel: INK / EMERALD / GOLD / IVORY / SAND.
 *
 * Regola Doc 02: nessun dato inventato. Ogni blocco rende SOLO ciò che la config
 * gli passa; se un dato manca (es. stato progetto, prove datate) il blocco/riga
 * non viene mostrato e il TODO resta tracciato in plan.md.
 */
import React from 'react';
import { ArrowRight, CalendarClock, Check, MapPin, FileText, ShieldCheck, Info, CheckCircle2, Mail, PhoneCall } from 'lucide-react';
import { NoObligationNote } from './NoObligationNote';

export const INK = '#081712';
export const EMERALD = '#0C2B21';
export const GOLD = '#D6B25E';
export const IVORY = '#F4EEE1';
export const SAND = '#CFC6B2';

const GOLD_BTN = 'aml-gold-btn rounded-full min-h-12 py-2.5 px-5 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.18em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2';
const GHOST_BTN = 'rounded-full min-h-12 py-2.5 px-5 text-[11px] sm:text-[12px] font-sans font-bold tracking-[0.12em] sm:tracking-[0.18em] uppercase text-center leading-snug inline-flex items-center justify-center gap-2 border transition-colors duration-150 hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D6B25E]';

/* ── Hero testuale sopra la piega: proposta concreta + localizzazione + stato + micro-proof ── */
export function IntroHero({ intro, onPrimary, testIdPrefix = 'aml' }) {
  if (!intro) return null;
  return (
    <section className="relative" data-testid={`${testIdPrefix}-intro`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 sm:pt-14 pb-8">
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }} data-testid={`${testIdPrefix}-intro-kicker`}>
          <MapPin className="w-3.5 h-3.5" aria-hidden="true" /> {intro.kicker}
        </div>
        <h1 className="mt-4 font-serif text-[2.1rem] leading-[1.06] sm:text-5xl lg:text-[3.6rem] tracking-tight max-w-3xl" style={{ color: IVORY }} data-testid={`${testIdPrefix}-intro-title`}>
          {intro.title}
        </h1>
        {intro.subtitle && (
          <p className="mt-4 text-[15px] sm:text-lg font-sans leading-relaxed max-w-2xl" style={{ color: SAND }} data-testid={`${testIdPrefix}-intro-subtitle`}>
            {intro.subtitle}
          </p>
        )}
        {Array.isArray(intro.facts) && intro.facts.length > 0 && (
          <dl className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`${testIdPrefix}-intro-facts`}>
            {intro.facts.filter((f) => f && f.value).map((f) => (
              <div key={f.label} className="rounded-2xl border px-4 py-3" style={{ borderColor: `${GOLD}40`, background: `${IVORY}0A` }} data-testid={`${testIdPrefix}-fact-${f.id || f.label}`}>
                <dt className="text-[10px] font-sans font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>{f.label}</dt>
                <dd className="mt-1 text-sm font-sans leading-snug" style={{ color: IVORY }}>{f.value}</dd>
                {f.note && <dd className="mt-0.5 text-[11px] font-sans" style={{ color: SAND }}>{f.note}</dd>}
              </div>
            ))}
          </dl>
        )}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
          <button type="button" onClick={onPrimary} className={`${GOLD_BTN} w-full sm:w-auto`} data-testid={`${testIdPrefix}-intro-cta`}>
            {intro.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
          {intro.ctaHint && <span className="text-xs font-sans" style={{ color: SAND }}>{intro.ctaHint}</span>}
        </div>
        <NoObligationNote theme="dark" className="mt-4 justify-start" testId={`${testIdPrefix}-intro-no-obligation`} />
      </div>
    </section>
  );
}

/* ── Micro-proof / "cosa puoi verificare" (solo elementi reali passati dalla config) ── */
export function ProofStrip({ proof, testIdPrefix = 'aml' }) {
  if (!proof || !Array.isArray(proof.items) || !proof.items.length) return null;
  return (
    <section className="relative" data-testid={`${testIdPrefix}-proof`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
        <div className="rounded-3xl border p-6 sm:p-7" style={{ borderColor: `${GOLD}40`, background: `${EMERALD}99`, backdropFilter: 'blur(12px)' }}>
          <div className="text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>{proof.kicker}</div>
          {proof.title && <h2 className="mt-2 font-serif text-2xl sm:text-3xl leading-tight" style={{ color: IVORY }}>{proof.title}</h2>}
          <ul className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            {proof.items.map((it) => (
              <li key={it.t} className="flex items-start gap-3" data-testid={`${testIdPrefix}-proof-item`}>
                <span className="mt-0.5 w-7 h-7 rounded-full grid place-items-center shrink-0" style={{ background: `${GOLD}22` }}>
                  {React.createElement(it.icon || Check, { className: 'w-3.5 h-3.5', style: { color: GOLD }, 'aria-hidden': 'true' })}
                </span>
                <div>
                  <div className="text-sm font-sans font-semibold" style={{ color: IVORY }}>{it.t}</div>
                  {it.d && <div className="mt-0.5 text-[13px] font-sans leading-snug" style={{ color: SAND }}>{it.d}</div>}
                  {it.date && <div className="mt-0.5 text-[11px] font-sans" style={{ color: GOLD }}>{it.date}</div>}
                </div>
              </li>
            ))}
          </ul>
          {proof.note && (
            <p className="mt-5 flex items-start gap-2 text-[12px] font-sans leading-snug" style={{ color: SAND }}>
              <Info className="w-4 h-4 shrink-0 mt-[1px]" style={{ color: GOLD }} aria-hidden="true" /> {proof.note}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

/* ── Percorso in 2 (o N) step: dossier/Zoom → verifica in loco ── */
export function StepPath({ path, onPrimary, testIdPrefix = 'aml' }) {
  if (!path || !Array.isArray(path.steps) || !path.steps.length) return null;
  return (
    <section id={path.id || 'percorso'} className="relative scroll-mt-20" data-testid={`${testIdPrefix}-path`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>{path.kicker}</div>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl leading-[1.08] tracking-tight max-w-3xl" style={{ color: IVORY }} data-testid={`${testIdPrefix}-path-title`}>{path.title}</h2>
        {path.intro && <p className="mt-3 text-sm sm:text-[15px] font-sans leading-relaxed max-w-2xl" style={{ color: SAND }}>{path.intro}</p>}
        <ol className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-4">
          {path.steps.map((s, i) => (
            <li key={s.t} className="rounded-3xl border p-6 relative overflow-hidden" style={{ borderColor: `${GOLD}40`, background: `${IVORY}08` }} data-testid={`${testIdPrefix}-path-step-${i + 1}`}>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full grid place-items-center font-serif text-lg" style={{ background: GOLD, color: INK }}>{String(i + 1).padStart(2, '0')}</span>
                <div className="text-[11px] font-sans font-bold tracking-[0.2em] uppercase" style={{ color: GOLD }}>{s.k}</div>
              </div>
              <h3 className="mt-4 font-serif text-xl sm:text-2xl leading-tight" style={{ color: IVORY }}>{s.t}</h3>
              <p className="mt-2 text-sm font-sans leading-relaxed" style={{ color: SAND }}>{s.d}</p>
              {Array.isArray(s.bullets) && s.bullets.length > 0 && (
                <ul className="mt-3 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[13px] font-sans" style={{ color: IVORY }}>
                      <Check className="w-4 h-4 shrink-0 mt-[1px]" style={{ color: GOLD }} aria-hidden="true" /> {b}
                    </li>
                  ))}
                </ul>
              )}
              {s.cta && (
                <button type="button" onClick={onPrimary} className={`${GOLD_BTN} mt-5 w-full sm:w-auto`} data-testid={`${testIdPrefix}-path-step-${i + 1}-cta`}>
                  {s.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
              {s.note && <p className="mt-3 text-[11px] font-sans" style={{ color: SAND }}>{s.note}</p>}
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ── Modello / condizioni dichiarate, con nota "soggette a verifica" obbligatoria ── */
export function ModelTerms({ model, testIdPrefix = 'aml' }) {
  if (!model || !Array.isArray(model.items) || !model.items.length) return null;
  return (
    <section id={model.id || 'modello'} className="relative scroll-mt-20" data-testid={`${testIdPrefix}-model`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>{model.kicker}</div>
        <h2 className="mt-2 font-serif text-3xl sm:text-4xl leading-[1.08] tracking-tight max-w-3xl" style={{ color: IVORY }} data-testid={`${testIdPrefix}-model-title`}>{model.title}</h2>
        {model.intro && <p className="mt-3 text-sm sm:text-[15px] font-sans leading-relaxed max-w-2xl" style={{ color: SAND }}>{model.intro}</p>}
        <dl className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {model.items.map((it) => (
            <div key={it.t} className="rounded-2xl border p-5" style={{ borderColor: `${GOLD}40`, background: `${IVORY}08` }} data-testid={`${testIdPrefix}-model-item`}>
              <dt className="flex items-center gap-2 text-sm font-sans font-semibold" style={{ color: IVORY }}>
                {React.createElement(it.icon || FileText, { className: 'w-4 h-4 shrink-0', style: { color: GOLD }, 'aria-hidden': 'true' })} {it.t}
              </dt>
              <dd className="mt-1.5 text-[13px] font-sans leading-relaxed" style={{ color: SAND }}>{it.d}</dd>
              {it.flag && <dd className="mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-sans font-bold tracking-[0.14em] uppercase" style={{ background: `${GOLD}1F`, color: GOLD }}>{it.flag}</dd>}
            </div>
          ))}
        </dl>
        <p className="mt-6 flex items-start gap-2 text-[12px] font-sans leading-snug rounded-2xl border px-4 py-3" style={{ color: SAND, borderColor: `${GOLD}40`, background: `${EMERALD}80` }} data-testid={`${testIdPrefix}-model-disclaimer`}>
          <ShieldCheck className="w-4 h-4 shrink-0 mt-[1px]" style={{ color: GOLD }} aria-hidden="true" /> {model.disclaimer}
        </p>
      </div>
    </section>
  );
}

/* ── Call high-value separata (Calendly CEO) ── */
export function CeoCallCard({ card, href, onClick, testIdPrefix = 'aml' }) {
  if (!card) return null;
  return (
    <section id="consulenza" className="relative scroll-mt-20" data-testid={`${testIdPrefix}-ceo-call`}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="rounded-3xl border p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 items-center" style={{ borderColor: `${GOLD}40`, background: `${IVORY}08` }}>
          <div>
            <div className="text-[11px] font-sans font-bold tracking-[0.25em] uppercase" style={{ color: GOLD }}>{card.kicker}</div>
            <h2 className="mt-2 font-serif text-2xl sm:text-3xl leading-tight" style={{ color: IVORY }} data-testid={`${testIdPrefix}-ceo-call-title`}>{card.title}</h2>
            <p className="mt-2 text-sm font-sans leading-relaxed max-w-xl" style={{ color: SAND }}>{card.text}</p>
          </div>
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={`${GHOST_BTN} w-full md:w-auto`} style={{ borderColor: GOLD, color: IVORY }} data-testid={`${testIdPrefix}-ceo-call-cta`}>
            <CalendarClock className="w-4 h-4" aria-hidden="true" /> {card.cta} <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── Conferma + prossimo step dopo il submit ── */
export function NextStepsSuccess({ success, calendly, onBooking, testIdPrefix = 'aml' }) {
  if (!success) return null;
  const icons = [Mail, PhoneCall, CalendarClock];
  return (
    <div className="rounded-2xl border p-7 sm:p-8" style={{ borderColor: `${GOLD}66`, background: `${EMERALD}CC`, backdropFilter: 'blur(14px)' }} data-testid={`${testIdPrefix}-form-success`}>
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-10 h-10 shrink-0" style={{ color: GOLD }} strokeWidth={1.4} aria-hidden="true" />
        <div>
          <div className="text-2xl font-serif" style={{ color: IVORY }} data-testid={`${testIdPrefix}-success-title`}>{success.title}</div>
          {success.subtitle && <p className="mt-1 text-sm font-sans" style={{ color: SAND }}>{success.subtitle}</p>}
        </div>
      </div>
      {Array.isArray(success.steps) && success.steps.length > 0 && (
        <ol className="mt-6 space-y-3" data-testid={`${testIdPrefix}-success-steps`}>
          {success.steps.map((s, i) => (
            <li key={s} className="flex items-start gap-3 text-sm font-sans leading-snug" style={{ color: IVORY }}>
              <span className="w-7 h-7 rounded-full grid place-items-center shrink-0 text-[11px] font-bold" style={{ background: `${GOLD}22`, color: GOLD }}>
                {React.createElement(icons[i] || Check, { className: 'w-3.5 h-3.5', 'aria-hidden': 'true' })}
              </span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      )}
      {success.bookingCta && (
        <>
          <a href={calendly} target="_blank" rel="noopener noreferrer" onClick={onBooking} className={`${GOLD_BTN} mt-7 w-full`} data-testid={`${testIdPrefix}-success-booking`}>
            <CalendarClock className="w-4 h-4" aria-hidden="true" /> {success.bookingCta}
          </a>
          {success.bookingNote && <p className="mt-2 text-center text-[12px] font-sans" style={{ color: SAND }}>{success.bookingNote}</p>}
        </>
      )}
      <NoObligationNote theme="dark" className="mt-4" testId={`${testIdPrefix}-success-no-obligation`} compact />
    </div>
  );
}

/* ── Barra CTA sticky su mobile (coerente con la CTA primaria) ── */
export function StickyCta({ label, onClick, visible = true, testIdPrefix = 'aml' }) {
  if (!visible || !label) return null;
  return (
    <div className="fixed inset-x-0 bottom-0 z-40 md:hidden px-4 pb-[max(env(safe-area-inset-bottom),12px)] pt-2" style={{ background: `linear-gradient(to top, ${INK} 60%, transparent)` }} data-testid={`${testIdPrefix}-sticky-cta`}>
      <button type="button" onClick={onClick} className={`${GOLD_BTN} w-full`} data-testid={`${testIdPrefix}-sticky-cta-btn`}>
        {label} <ArrowRight className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}
